import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useData, useSession, orgID, api } from '@yingbaba/api';
import { digitalPurchasePath } from '@yingbaba/utils';
import type { DigitalOrderView, DigitalProjectAssetView, Page } from '@yingbaba/types';
import {
  Button,
  ButtonLink,
  useToast,
  Empty,
  ErrorNotice,
  FileAttachment,
  Modal,
  Notice,
  Pagination,
  QueryState,
} from '@yingbaba/ui';

type Props = { projectID: string; ownerID: string; write: boolean };
export function ProjectLicensedAssets(props: Props) {
  const session = useSession();
  const [params] = useSearchParams();
  const role = session.data?.organizations.find((org) => org.id === orgID())?.role || '';
  const raw = params.get('license_order') || '';
  const orderID = /^[0-9A-HJKMNP-TV-Z]{26}$/.test(raw) ? raw : '';
  return (
    <LicensedAssetsPanel
      key={`${props.projectID}:${orgID()}:${session.data?.account.id}:${role}:${props.write}:${raw}`}
      {...props}
      targetOrder={orderID}
      invalidTarget={!!raw && !orderID}
    />
  );
}
function LicensedAssetsPanel({
  projectID,
  ownerID,
  write,
  targetOrder,
  invalidTarget,
}: Props & { targetOrder: string; invalidTarget: boolean }) {
  const session = useSession();
  const canAdd =
    write &&
    ownerID === orgID() &&
    ['owner', 'admin'].includes(
      session.data?.organizations.find((o) => o.id === orgID())?.role || '',
    );
  const [page, setPage] = useState(1);
  const assets = useData<Page<DigitalProjectAssetView>>(
    `/projects/${projectID}/licensed-assets?page=${page}${targetOrder ? `&order_id=${targetOrder}` : ''}`,
  );
  const linked = assets.data?.items.some((asset) => asset.order_id === targetOrder);
  const [open, setOpen] = useState(false),
    [candidatePage, setCandidatePage] = useState(1);
  const candidates = useData<Page<DigitalOrderView>>(
    `/projects/${projectID}/licensed-assets/candidates?page=${candidatePage}${targetOrder ? `&order_id=${targetOrder}` : ''}`,
    (open || (!!targetOrder && !!assets.data && !assets.isError && !linked)) && canAdd,
  );
  const [selected, setSelected] = useState<DigitalOrderView | null>(null),
    [confirmed, setConfirmed] = useState(false);
  const [evidence, setEvidence] = useState<DigitalProjectAssetView | null>(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  const pending = useRef(false);
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const root = useRef<HTMLElement | null>(null);
  const handled = useRef(false);
  const alive = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const organization = useRef(orgID());
  const toast = useToast();
  const current = () => alive.current && organization.current === orgID();
  const ready = candidates.data?.items.find((order) => order.id === targetOrder);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (!targetOrder || handled.current || !assets.data || assets.isError) return;
    if (linked) {
      handled.current = true;
      return;
    }
    if (canAdd && ready && !candidates.isError) {
      handled.current = true;
      setSelected(ready);
      setConfirmed(false);
      setError(null);
      setOpen(true);
    }
  }, [targetOrder, assets.data, assets.isError, linked, canAdd, ready, candidates.isError]);
  useEffect(() => {
    if (
      (!targetOrder && !invalidTarget) ||
      (!assets.data && !assets.isError) ||
      (targetOrder &&
        !assets.isError &&
        !linked &&
        canAdd &&
        !candidates.isError &&
        (!candidates.data || ready))
    )
      return;
    const frame = requestAnimationFrame(() => {
      root.current?.scrollIntoView({ block: 'start' });
      root.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [
    targetOrder,
    invalidTarget,
    !!assets.data,
    assets.isError,
    linked,
    canAdd,
    !!candidates.data,
    candidates.isError,
    !!ready,
  ]);
  function viewAll() {
    setParams(
      (value) => {
        value.delete('license_order');
        return value;
      },
      { replace: true },
    );
  }
  function closePicker() {
    if (pending.current) return;
    setOpen(false);
    if (targetOrder)
      requestAnimationFrame(() => {
        root.current?.scrollIntoView({ block: 'start' });
        root.current?.focus({ preventScroll: true });
      });
  }
  async function add() {
    if (!canAdd || !selected || !confirmed || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      controller.current = new AbortController();
      await api(`/projects/${projectID}/licensed-assets`, {
        method: 'POST',
        signal: controller.current.signal,
        body: JSON.stringify({ order_id: selected.id, share_confirmed: true }),
      });
      if (!current()) return;
      await qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[1] === organization.current &&
          typeof q.queryKey[0] === 'string' &&
          (q.queryKey[0] === `/projects/${projectID}` ||
            q.queryKey[0].startsWith(`/projects/${projectID}/licensed-assets`)),
      });
      if (!current()) return;
      toast('素材已用于本项目，协作者可按许可预览和下载');
      setPage(1);
      setOpen(false);
      setSelected(null);
      setConfirmed(false);
    } catch (e) {
      if (current()) setError(e);
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  }
  return (
    <section
      ref={root}
      tabIndex={-1}
      aria-label="项目授权素材"
      className="panel project-licensed-assets"
    >
      <div className="panel-title">
        <div>
          <h3>项目授权素材</h3>
          <p className="muted small">将组织已购素材用于本项目，保留原始版本和授权凭证。</p>
        </div>
        {canAdd && !linked && (!targetOrder || !!ready) && (
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(true);
              setCandidatePage(1);
              setSelected(ready || null);
              setConfirmed(false);
              setError(null);
            }}
          >
            {targetOrder ? '继续添加此素材' : '从已购素材添加'}
          </Button>
        )}
      </div>
      {(targetOrder || invalidTarget) && (
        <div className="project-license-handoff" aria-label="本次授权素材">
          <div>
            <strong>
              {invalidTarget
                ? '素材链接不完整'
                : linked
                  ? '这份素材已用于本项目'
                  : '将已购素材用于本项目'}
            </strong>
            <p className="small muted">
              {invalidTarget
                ? '请查看项目已关联的授权素材，或从已购素材中重新选择。'
                : assets.isLoading || (canAdd && candidates.isLoading)
                  ? '正在核对素材与项目…'
                  : linked
                    ? '可在下方查看授权凭证，并按当前许可预览和下载。'
                    : !canAdd
                      ? '请由采购组织管理员核对并添加这份授权，添加后项目成员即可使用。'
                      : ready
                        ? ready.title
                        : candidates.isError || assets.isError
                          ? '素材信息暂时无法读取，请重新核对。'
                          : '暂未找到可添加的授权。请确认购买已完成、授权有效且与本项目一致。'}
            </p>
          </div>
          <div className="row wrap">
            <Button variant="secondary" disabled={busy} onClick={viewAll}>
              查看全部授权素材
            </Button>
            {targetOrder && canAdd && !ready && !linked && (
              <ButtonLink variant="ghost" to={digitalPurchasePath(targetOrder, 'organization')}>
                查看购买记录
              </ButtonLink>
            )}
            {targetOrder && (assets.isError || (canAdd && candidates.isError)) && (
              <Button
                variant="secondary"
                onClick={() => {
                  void assets.refetch();
                  if (canAdd) void candidates.refetch();
                }}
              >
                重新核对
              </Button>
            )}
          </div>
          {canAdd && <ErrorNotice error={candidates.error} />}
        </div>
      )}
      <QueryState query={assets}>
        {!assets.data?.items.length && (
          <Empty
            title="还没有关联授权素材"
            description="组织购买的商业或扩展授权，可由采购组织管理员添加到对应的具名项目。"
          />
        )}
        <div className="stack">
          {assets.data?.items.map((a) => (
            <article className="project-file-version" key={a.id}>
              <div className="row spread wrap">
                <div>
                  <strong>{a.contract.title}</strong>
                  <p className="muted small">
                    {a.contract.license.name} · 被授权主体：{a.contract.licensee_name}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEvidence(a)}>
                  查看授权
                </Button>
              </div>
              {!a.available ? (
                <Notice>{a.unavailable_reason || '授权暂不可用，请联系采购组织。'}</Notice>
              ) : (
                a.contract.files.map((f) => (
                  <FileAttachment
                    key={f.id}
                    name={f.name}
                    mime={f.mime}
                    size={f.size}
                    previewPath={`/projects/${projectID}/licensed-assets/${a.order_id}/files/${f.id}/preview`}
                    downloadPath={`/projects/${projectID}/licensed-assets/${a.order_id}/files/${f.id}/download`}
                  />
                ))
              )}
              <small className="muted">
                限本项目内协作使用；源文件不可跨项目复用、转售或用于 AI 训练。
              </small>
            </article>
          ))}
        </div>
      </QueryState>
      <Pagination page={page} size={24} total={assets.data?.total || 0} onPage={setPage} />
      <Modal
        title="从已购素材添加"
        description="只展示本组织购买、授权有效且与本项目匹配的素材。每份许可只关联一个项目。"
        open={open}
        busy={busy}
        onOpenChange={(v) => {
          if (!v) closePicker();
        }}
        footer={
          <Button disabled={!selected || !confirmed} loading={busy} onClick={add}>
            添加到项目
          </Button>
        }
      >
        <QueryState query={candidates}>
          {!candidates.data?.items.length && (
            <Empty
              title="没有可添加的授权素材"
              description="购买时请选择本项目，或填写与本项目一致的具名项目名称。个人购买的素材不属于组织采购。"
            />
          )}
          {candidates.data?.items.map((o) => (
            <label className="list-item file-library-option project-license-option" key={o.id}>
              <input
                type="radio"
                name="purchased-resource"
                checked={selected?.id === o.id}
                onChange={() => {
                  setSelected(o);
                  setConfirmed(false);
                }}
                disabled={busy}
              />
              <span>
                <strong>{o.title}</strong>
                <span className="muted small">
                  {o.contract.license.name} · {o.licensee_name} · {o.project_name}
                </span>
              </span>
            </label>
          ))}
        </QueryState>
        <fieldset disabled={busy} className="project-license-pagination">
          <Pagination
            page={candidatePage}
            size={24}
            total={candidates.data?.total || 0}
            onPage={setCandidatePage}
          />
        </fieldset>
        {selected && (
          <details>
            <summary>查看本次授权范围</summary>
            <p>{selected.contract.license.scope}</p>
            <p>{selected.contract.license.restrictions}</p>
          </details>
        )}
        <label className="project-tool-consent">
          <input
            type="checkbox"
            checked={confirmed}
            disabled={!selected || busy}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          我确认这是许可约定的具名项目，并有权让当前及后续加入的项目协作者按许可使用素材。
        </label>
        <Notice>
          添加后，项目协作者可以预览和下载这份授权的文件，无法访问组织的其他购买记录。已下载的文件无法追回，请谨慎邀请协作者。
        </Notice>
        <ErrorNotice error={error} />
      </Modal>
      <Modal
        title="项目素材授权"
        open={!!evidence}
        onOpenChange={(v) => {
          if (!v) setEvidence(null);
        }}
      >
        {evidence && (
          <div className="stack">
            <p>
              <strong>{evidence.contract.title}</strong>
            </p>
            <p>授权编号：{evidence.license_number}</p>
            <p>被授权主体：{evidence.contract.licensee_name}</p>
            <p>具名项目：{evidence.contract.project_name}</p>
            <p>
              {evidence.contract.license.name} ·{' '}
              {evidence.contract.territory === 'CN' ? '中国大陆' : '全球'}
            </p>
            <p>{evidence.contract.license.scope}</p>
            <p>{evidence.contract.license.restrictions}</p>
            <p>状态：{evidence.available ? '有效' : evidence.unavailable_reason}</p>
            <details>
              <summary>完整许可条款与文件版本</summary>
              <p className="project-license-terms">{evidence.contract.terms}</p>
              {evidence.contract.files.map((f) => (
                <p className="license-file-hash" key={f.id}>
                  {f.name}
                  <br />
                  SHA-256 {f.sha256}
                </p>
              ))}
            </details>
          </div>
        )}
      </Modal>
    </section>
  );
}
